import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useSessionStore } from '@/app/session-store'

import { headerEnginesQuery, type HeaderEngine } from './queries'
import { useEngineSelectionStore } from './selection-store'

/**
 * The course's engines and the one selected in the header. Every engine-scoped page
 * reads the selection from here, so no page has its own engine picker.
 *
 * The selected engine is the one the operator picked in the header, while it is still on the
 * list; otherwise the engine list's own selected engine (`selectedEngineId`, the newest).
 */
export function useSelectedEngine() {
  const { courseId } = storeToRefs(useSessionStore())
  const { pickedEngineId } = storeToRefs(useEngineSelectionStore())

  const engines = useQuery(
    computed(() => ({
      ...headerEnginesQuery(courseId.value ?? 0),
      enabled: courseId.value !== null,
    })),
  )

  const list = computed<HeaderEngine[]>(() => engines.data.value?.engines ?? [])
  const selectedEngine = computed<HeaderEngine | null>(() => {
    const find = (id: number | null | undefined) =>
      list.value.find((engine) => engine.id === id) ?? null
    return find(pickedEngineId.value) ?? find(engines.data.value?.selectedEngineId)
  })

  return {
    courseId,
    engines: list,
    selectedEngine,
    hasEngines: computed(() => list.value.length > 0),
    isPending: engines.isPending,
    isError: engines.isError,
  }
}
